import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PermissionGuard, AccessDenied } from '@/common/components';
import { PERMISSIONS } from '@/common/config/permissions';
import { CRMPipelinePage } from '../routes/CRMPipelinePage';

function capturePipelineScroll(root) {
  if (!root) return null;
  const dash = root.querySelector('#dashboard-scroll-container');
  const kanban = root.querySelector('[data-pipeline-kanban]');
  const cols = {};
  root.querySelectorAll('[id^="stage-column-scroll-"]').forEach((el) => {
    cols[el.id] = el.scrollTop;
  });
  return {
    dash: dash?.scrollTop ?? 0,
    kanban: kanban?.scrollLeft ?? 0,
    cols,
  };
}

function restorePipelineScroll(root, memo) {
  if (!root || !memo) return;
  const dash = root.querySelector('#dashboard-scroll-container');
  const kanban = root.querySelector('[data-pipeline-kanban]');
  if (dash) dash.scrollTop = memo.dash;
  if (kanban) kanban.scrollLeft = memo.kanban;
  Object.entries(memo.cols || {}).forEach(([id, top]) => {
    const el = root.querySelector(`[id="${id}"]`);
    if (el) el.scrollTop = top;
  });
}

/**
 * Keep CRMPipelinePage mounted while viewing Inquiry Details from the
 * pipeline. Uses display:none (after capturing scroll) so Overview is not
 * a hybrid of both pages. Unhide restores the same DOM + scroll.
 */
export function CRMPipelineKeepAlive({ children }) {
  const location = useLocation();
  const isPipeline = location.pathname === '/crm-pipeline';
  const isOverview = location.pathname === '/crm-contacts/overview';
  const fromPipeline =
    typeof location.state?.from === 'string' &&
    location.state.from.startsWith('/crm-pipeline');

  const [parked, setParked] = useState(isPipeline);
  const [visuallyHidden, setVisuallyHidden] = useState(false);
  const shellRef = useRef(null);
  const scrollMemoRef = useRef(null);

  useLayoutEffect(() => {
    if (isPipeline || (isOverview && fromPipeline)) {
      setParked(true);
      return;
    }
    if (!isOverview) {
      setParked(false);
    }
  }, [isPipeline, isOverview, fromPipeline]);

  const keepMounted = isPipeline || (isOverview && parked);
  const shouldPark = keepMounted && !isPipeline;

  useLayoutEffect(() => {
    if (shouldPark) {
      scrollMemoRef.current = capturePipelineScroll(shellRef.current);
      setVisuallyHidden(true);
      return;
    }
    setVisuallyHidden(false);
  }, [shouldPark]);

  useLayoutEffect(() => {
    if (visuallyHidden || !keepMounted) return;
    restorePipelineScroll(shellRef.current, scrollMemoRef.current);
  }, [visuallyHidden, keepMounted]);

  return (
    <div className="relative w-full h-full min-h-0 flex flex-col flex-1">
      {keepMounted && (
        <div
          ref={shellRef}
          hidden={visuallyHidden}
          className={
            visuallyHidden
              ? 'hidden'
              : 'relative w-full h-full min-h-0 flex flex-col flex-1'
          }
          aria-hidden={visuallyHidden}
        >
          <PermissionGuard
            permission={PERMISSIONS.PIPELINES.VIEW}
            fallback={<AccessDenied />}
          >
            <CRMPipelinePage />
          </PermissionGuard>
        </div>
      )}
      {!isPipeline && (
        <div className="relative z-10 w-full h-full min-h-0 flex flex-col flex-1">
          {children}
        </div>
      )}
    </div>
  );
}
